import { getCertificationTemplateBase64 } from "../../services/certification";
import { getExamResultsByUserEmail } from "../../services/exam-result";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { useTranslation } from "react-i18next";
import {formatDateSlash, getCurrentLanguageKey, getCurrentUserEmailAddress } from "../../utils/util";
import React, { useEffect, useState, useCallback } from "react";
import UserCertificationCard from "../Certification/UserCertificationCard";

const UserDashboardCertificationList = () => {
	const [certificationTemplateBase64, setCertificationTemplateBase64] = useState(null);
	const [userCertifications, setUserCertifications] = useState([]);

	const {t, i18n: { changeLanguage }} = useTranslation();

	useEffect(() => {
		changeLanguage(getCurrentLanguageKey());

		(async () => {
			const [certifications, template] = await Promise.allSettled([
				getExamResultsByUserEmail(getCurrentUserEmailAddress()),
				getCertificationTemplateBase64(),
			]);

			setUserCertifications(certifications.value ?? []);
			setCertificationTemplateBase64(template.value ?? null);
		})();
	}, [changeLanguage]);

	const handleCertificationDownload = useCallback(async (certification) => {
		const defaultColor = rgb(1, 1, 1);
		const pdfDoc = await PDFDocument.create();

		const page = pdfDoc.addPage([842, 595]);
		const [ helveticaFont, helveticaBoldFont, helveticaObliqueFont, pngImage ] = await Promise.all([
			pdfDoc.embedFont(StandardFonts.Helvetica),
			pdfDoc.embedFont(StandardFonts.HelveticaBold),
			pdfDoc.embedFont(StandardFonts.HelveticaOblique),
			pdfDoc.embedPng(certificationTemplateBase64),
		]);

		page.drawImage(pngImage, {
			height: page.getHeight(),
			width: page.getWidth(),
			x: 0,
			y: 0,
		});

		page.drawText(t('this-document-recognizes-that'), {
			color: defaultColor,
			font: helveticaFont,
			size: 16,
			x: 60,
			y: 455,
		});

		page.drawText(`${certification.firstName} ${certification.lastName}`, {
			color: defaultColor,
			font: helveticaBoldFont,
			size: 35,
			x: 57,
			y: 405,
		});

		page.drawText(t('has-been-certified-in'), {
			color: defaultColor,
			font: helveticaFont,
			size: 16,
			x: 60,
			y: 355,
		});

		page.drawText(certification.testName, {
			color: defaultColor,
			font: helveticaBoldFont,
			lineHeight: 48,
			maxWidth: 500,
			size: 35,
			x: 59,
			y: 300,
		});

		page.drawText(formatDateSlash(certification.date), {
			color: defaultColor,
			font: helveticaBoldFont,
			size: 10,
			x: 420,
			y: 90,
		});

		page.drawText(t('this-certification-is-valid-for-2-years-from-the-date-of-issue'), {
			color: defaultColor,
			font: helveticaObliqueFont,
			size: 8,
			x: 58,
			y: 40,
		});

		const blob = new Blob([await pdfDoc.save()], { type: "application/pdf" });
		const link = document.createElement("a");

		link.href = URL.createObjectURL(blob);
		link.download = `${certification.testName}.pdf`;
		link.click();
	}, [t, certificationTemplateBase64]);

	return (
		<div className="certification-list">
			{userCertifications.map(cert => (
				<UserCertificationCard
					key={cert.id}
					certification={cert}
					certificationTemplate={certificationTemplateBase64}
					handleCertificationDownload={handleCertificationDownload}
				/>
			))}
		</div>
	);
};

export default UserDashboardCertificationList;